import React, { useCallback } from 'react';
import { CronofyOptions } from '../../../../../components/cronofy/CronofyOptions';
import { Interview } from '../../../../../entities/applicant_tracking/Interview';
import ScheduleInterviewPage from '../index';
import { InterviewService } from '../../../../../services/applicant_tracking/InterviewService';
import { EventSlot } from '../../../../../components/cronofy/CronofyDatePicker/EventSlot';
import { Timedelta } from '../../../../../utils/timedelta';
import { User } from '../../../../../entities/User';
import { VcsProvider } from '../../../../../enums/IntegrationProvider/VcsProvider';
import { InterviewerParticipant } from '../../../../../entities/applicant_tracking/InterviewerParticipant';
import { appendCurrentRedirectUri } from '../../../../../utils/url';
import { addReturnTitle } from '../../../../../utils/applicant_tracking/actions/schedulingActions';
import { Candidate } from '../../../../../entities/applicant_tracking/Candidate';
import { UpsertEmailTemplateDto } from '../../../../EmailTemplates/SetupEmailTemplatePage/UpsertEmailTemplateDto';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../../config/queryClient';
import { FeatureFlagContext } from '../../../../../contexts/FeatureFlagContext';

interface PropTypes {
  applicationId: number;
  candidate: Candidate;
  cronofyDatePickerOptions: CronofyOptions;
  meetingOrganizer: User;
  interview: Interview;
  meetingProviders: VcsProvider[];
  participants: User[];
  personalizingSchedulingInvitesV1FfEnabled: boolean;
  personalizingSchedulingInvitesV2FfEnabled: boolean;
}

export default function RescheduleInterview(props: PropTypes) {
  const onConfirm = useCallback(
    async (
      eventSlot: EventSlot,
      participants: InterviewerParticipant[],
      meetingProvider: string,
      calendarEventTemplateId: number,
      calendarEventTemplateDto: UpsertEmailTemplateDto,
    ) => {
      try {
        const interview = await InterviewService.update(
          props.interview.id,
          participants.map((p) => p.user.id),
          eventSlot.slot.start,
          eventSlot.slot.end,
          eventSlot.tzid,
          meetingProvider,
          calendarEventTemplateId,
          calendarEventTemplateDto,
        );

        const path = appendCurrentRedirectUri(
          `/applicant_tracking/interviews/${interview.id}/schedule_success`,
        );

        window.location.href = addReturnTitle(path);
      } catch (e) {
        window.location.href = appendCurrentRedirectUri(
          `/applicant_tracking/interviews/schedule_error?application_id=${props.interview.applicationId}`,
        );
      }
    },
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagContext.Provider
        value={{
          personalizingSchedulingInvitesV1FfEnabled:
            props.personalizingSchedulingInvitesV1FfEnabled,
          personalizingSchedulingInvitesV2FfEnabled:
            props.personalizingSchedulingInvitesV2FfEnabled,
        }}
      >
        <ScheduleInterviewPage
          meetingOrganizer={props.meetingOrganizer}
          applicationIds={[props.applicationId]}
          title='Reschedule your interview'
          description='At least one participant needs to have the calendar connection'
          cronofyDatePickerOptions={props.cronofyDatePickerOptions}
          candidate={props.candidate}
          participants={props.participants}
          interviewStage={props.interview.interviewStage}
          meetingProviders={props.meetingProviders}
          meetingProvider={props.interview.meetingProvider}
          durationMinutes={Timedelta.fromMs(props.interview.duration).toMins()}
          onConfirm={onConfirm}
          candidateView={false}
          selfScheduleView={false}
          allowChangingMeetingOrganizer={false}
          allowIgnoreParticipantCalendar={true}
          interview={props.interview}
          personalizingSchedulingInvitesV1FfEnabled={
            props.personalizingSchedulingInvitesV1FfEnabled
          }
        />
      </FeatureFlagContext.Provider>
    </QueryClientProvider>
  );
}
